import React from "react";
import {
  BriefcaseBusiness,
  CalendarDays,
  Euro,
  Filter,
  Plus,
  Search,
  Wrench,
} from "lucide-react";
import { usePanelTheme } from "../context/themeContext";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import StatCard from "../components/StatCard";
import UIButton from "../components/UIButton";
import UIInput from "../components/UIInput";
import { notify } from "../utils/notify";
import useProjectsData, { formatMoney, statusOptions } from "../hooks/useProjectsData";
import ProjectList from "../components/projects/ProjectList";
import ProjectDetailsPanel from "../components/projects/ProjectDetailsPanel";
import ProjectFormModal from "../components/projects/ProjectFormModal";

const ui = {
  sq: {
    title: "Projects / Baustellen",
    subtitle: "Menaxho projektet e renovimit, buxhetin, statusin dhe fitimin.",
    newProject: "Projekt i ri",
    editProject: "Edito projektin",
    totalProjects: "Totali projekteve",
    activeProjects: "Në progres",
    plannedProjects: "Të planifikuara",
    totalProfit: "Fitimi total",
    budgetMinusCost: "Buxhet minus kosto",
    allStatuses: "Të gjitha statuset",
    searchPlaceholder: "Kërko projekt, klient ose adresë",
    projectList: "Lista e projekteve",
    filteredProjects: "projekte sipas filtrit",
    noProjects: "Nuk ka projekte ende",
    noProjectsText: "Shto projektin e parë dhe do të shfaqet këtu.",
    noResults: "Nuk u gjet asnjë projekt",
    noResultsText: "Provo filtër ose kërkim tjetër.",
    status: "Statusi",
    planned: "Planned",
    in_progress: "In Progress",
    completed: "Finished",
    paused: "Paused",
    clientLink: "Lidh klient (opsionale)",
    noLinkedClient: "Pa klient të lidhur",
    clientName: "Emri i klientit",
    projectName: "Emri i projektit",
    address: "Adresa e objektit",
    budget: "Buxheti / të ardhurat",
    startDate: "Data e fillimit",
    endDate: "Data e përfundimit",
    laborCost: "Kosto e punës",
    materialCost: "Kosto materiali",
    otherCost: "Kosto tjera",
    totalHours: "Orë totale",
    workersCount: "Punëtorë",
    materialsCount: "Materiale",
    invoiceCount: "Fatura / dokumente",
    notes: "Shënime",
    notesPlaceholder: "Përshkrimi i projektit, detaje, hapat tjerë...",
    cancel: "Anulo",
    save: "Ruaj projektin",
    update: "Përditëso projektin",
    confirmDelete: "A dëshiron ta fshish këtë projekt?",
    projectSaved: "Projekti u ruajt me sukses.",
    projectUpdated: "Projekti u përditësua me sukses.",
    projectDeleted: "Projekti u fshi me sukses.",
    overview: "Përmbledhje",
    workers: "Punëtorë",
    materials: "Materiale",
    invoices: "Fatura",
    expenses: "Shpenzime",
    timesheets: "Timesheets",
    profit: "Profit",
    liveSync: "Lidhje automatike",
    linkedOffers: "Oferta të lidhura",
    linkedTimesheets: "Timesheets të lidhura",
    linkedInvoices: "Fatura të lidhura",
    linkedEvents: "Termine të lidhura",
    noLinkedRecords: "Nuk ka regjistrime të lidhura ende.",
    liveCosts: "Kosto live",
    trackedHours: "Orë të regjistruara",
    meetings: "termine",
    invoiceTotal: "Totali i faturës",
    totalLinkedRevenue: "Të ardhura të lidhura",
    details: "Detaje projekti",
    totalCost: "Kosto totale",
    estimatedProfit: "Fitimi i llogaritur",
    createdAt: "Krijuar",
    saveError: "Gabim gjatë ruajtjes së projektit.",
    loadError: "Gabim gjatë leximit të projekteve.",
    requiredName: "Shkruaj emrin e projektit.",
  },
  de: {
    title: "Projekte / Baustellen",
    subtitle: "Verwalte Renovierungsprojekte, Budget, Status und Gewinn.",
    newProject: "Neues Projekt",
    editProject: "Projekt bearbeiten",
    totalProjects: "Gesamtprojekte",
    activeProjects: "In Arbeit",
    plannedProjects: "Geplant",
    totalProfit: "Gesamtgewinn",
    budgetMinusCost: "Budget minus Kosten",
    allStatuses: "Alle Status",
    searchPlaceholder: "Projekt, Kunde oder Adresse suchen",
    projectList: "Projektliste",
    filteredProjects: "Projekte nach Filter",
    noProjects: "Noch keine Projekte",
    noProjectsText: "Erstelle das erste Projekt und es erscheint hier.",
    noResults: "Keine Projekte gefunden",
    noResultsText: "Versuche einen anderen Filter oder eine andere Suche.",
    status: "Status",
    planned: "Geplant",
    in_progress: "In Arbeit",
    completed: "Abgeschlossen",
    paused: "Pausiert",
    clientLink: "Kunde verknüpfen (optional)",
    noLinkedClient: "Kein verknüpfter Kunde",
    clientName: "Kundenname",
    projectName: "Projektname",
    address: "Baustellenadresse",
    budget: "Budget / Einnahmen",
    startDate: "Startdatum",
    endDate: "Enddatum",
    laborCost: "Arbeitskosten",
    materialCost: "Materialkosten",
    otherCost: "Sonstige Kosten",
    totalHours: "Gesamtstunden",
    workersCount: "Mitarbeiter",
    materialsCount: "Materialien",
    invoiceCount: "Rechnungen / Dokumente",
    notes: "Notizen",
    notesPlaceholder: "Projektbeschreibung, Details, nächste Schritte...",
    cancel: "Abbrechen",
    save: "Projekt speichern",
    update: "Projekt aktualisieren",
    confirmDelete: "Dieses Projekt wirklich löschen?",
    projectSaved: "Projekt wurde gespeichert.",
    projectUpdated: "Projekt wurde aktualisiert.",
    projectDeleted: "Projekt wurde gelöscht.",
    overview: "Übersicht",
    workers: "Mitarbeiter",
    materials: "Materialien",
    invoices: "Rechnungen",
    expenses: "Ausgaben",
    timesheets: "Zeiterfassung",
    profit: "Gewinn",
    liveSync: "Automatische Verknüpfung",
    linkedOffers: "Verknüpfte Angebote",
    linkedTimesheets: "Verknüpfte Zeiten",
    linkedInvoices: "Verknüpfte Rechnungen",
    linkedEvents: "Verknüpfte Termine",
    noLinkedRecords: "Noch keine verknüpften Einträge.",
    liveCosts: "Live-Kosten",
    trackedHours: "Erfasste Stunden",
    meetings: "Termine",
    invoiceTotal: "Rechnungssumme",
    totalLinkedRevenue: "Verknüpfter Umsatz",
    details: "Projektdetails",
    totalCost: "Gesamtkosten",
    estimatedProfit: "Berechneter Gewinn",
    createdAt: "Erstellt",
    saveError: "Fehler beim Speichern des Projekts.",
    loadError: "Fehler beim Laden der Projekte.",
    requiredName: "Bitte einen Projektnamen eingeben.",
  },
};

export default function Projects({ lang = "sq", t = {} }) {
  const text = ui[lang] || ui.sq;
  const { theme, currentTheme } = usePanelTheme();
  const data = useProjectsData({ text, notify });

  return (
    <PageShell>
      <div className="space-y-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2 className="text-2xl font-black tracking-tight md:text-3xl">{text.title}</h2>
            <p className={`mt-1 text-sm ${currentTheme.muted}`}>{text.subtitle}</p>
          </div>
          <UIButton onClick={data.openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            {text.newProject}
          </UIButton>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard title={text.totalProjects} value={String(data.stats.totalProjects)} subtitle={data.projects.length ? "" : text.noProjectsText} icon={BriefcaseBusiness} theme={theme} />
          <StatCard title={text.activeProjects} value={String(data.stats.activeProjects)} subtitle={text.in_progress} icon={Wrench} theme={theme} />
          <StatCard title={text.plannedProjects} value={String(data.stats.plannedProjects)} subtitle={text.planned} icon={CalendarDays} theme={theme} />
          <StatCard title={text.totalProfit} value={formatMoney(data.stats.totalProfit)} subtitle={text.budgetMinusCost} icon={Euro} theme={theme} accent />
        </div>

        <PanelCard>
          <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
            <div className="relative flex-1">
              <Search className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`} />
              <UIInput value={data.search} onChange={(e) => data.setSearch(e.target.value)} placeholder={text.searchPlaceholder} className="pl-11" />
            </div>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="relative min-w-[200px]">
                <Filter className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`} />
                <select
                  value={data.statusFilter}
                  onChange={(e) => data.setStatusFilter(e.target.value)}
                  className={`h-12 w-full rounded-2xl border pl-11 pr-4 text-sm outline-none transition ${theme === "dark" ? "border-slate-700 bg-slate-900 text-slate-100" : "border-slate-200 bg-slate-50 text-slate-900"}`}
                >
                  <option value="all">{text.allStatuses}</option>
                  {statusOptions.map((status) => (
                    <option key={status} value={status}>{text[status]}</option>
                  ))}
                </select>
              </div>
              <UIButton onClick={data.openCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                {text.newProject}
              </UIButton>
            </div>
          </div>
        </PanelCard>

        <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
          <PanelCard>
            <ProjectList
              theme={theme}
              text={text}
              loading={data.loadingProjects}
              filteredProjects={data.filteredProjects}
              projects={data.projects}
              selectedProjectId={data.selectedProjectId}
              onSelect={data.setSelectedProjectId}
              onEdit={data.openEdit}
              onDelete={data.handleDelete}
              t={t}
            />
          </PanelCard>

          <PanelCard>
            <ProjectDetailsPanel
              theme={theme}
              text={text}
              selectedProject={data.selectedProject}
              projectDetails={data.projectDetails}
              activeTab={data.activeTab}
              setActiveTab={data.setActiveTab}
              detailProfit={data.detailProfit}
              loadingDetails={data.loadingDetails}
            />
          </PanelCard>
        </div>
      </div>

      <ProjectFormModal
        open={data.modalOpen}
        onClose={() => data.setModalOpen(false)}
        onSave={data.handleSave}
        form={data.form}
        setForm={data.setForm}
        clients={data.clients}
        editingId={data.editingProject?.id}
        text={text}
        theme={theme}
      />
    </PageShell>
  );
}
